import React, {useEffect, useState} from 'react';
import {Button, Form, Table} from 'react-bootstrap';
import {
  addUsd,
  costPerReel,
  type StudioCostBucket,
  type StudioCostJob,
  type StudioCostQuery,
  type StudioCostReport,
} from '../../../data/models/StudioCosts';
import {adminPanelText as text} from '../adminPanelText';
import type {StudioCostsResult} from '../store/AdminPanelStore';

/** The report covers the last 30 days by default. */
const DEFAULT_RANGE_DAYS = 30;
/** The longest range the API reports on. */
const MAX_RANGE_DAYS = 92;
/** The most jobs the API lists. */
const JOBS_TAKE = 200;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface DateRange {
  /** Local calendar dates, YYYY-MM-DD; both days are included. */
  from: string;
  to: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const dateInput = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
/** Midnight at the start of a YYYY-MM-DD day, in the admin's time zone. */
function startOfDay(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** The last `days` days, today included. */
export function defaultRange(
  today: Date,
  days = DEFAULT_RANGE_DAYS,
): DateRange {
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  from.setDate(from.getDate() - (days - 1));
  return {from: dateInput(from), to: dateInput(today)};
}

/** Why the range cannot be reported on; null when it can. */
export function rangeProblem(range: DateRange): string | null {
  const start = startOfDay(range.from);
  const end = startOfDay(range.to);
  if (!start || !end || start > end) return text.rangeReversed;
  const days = Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1;
  if (days > MAX_RANGE_DAYS) return text.rangeTooLong(MAX_RANGE_DAYS);
  // The API measures the time between the instants sent, not calendar days: a range the
  // clocks go back in is an hour longer than its days.
  const {from, to} = costQuery(range);
  return Date.parse(to!) - Date.parse(from!) > MAX_RANGE_DAYS * DAY_MS
    ? text.rangeOverClockChange(MAX_RANGE_DAYS)
    : null;
}

/**
 * The report's filters for a range: from the start of its first day to the start of the day
 * after its last, in the admin's time zone, sent as ISO instants (`to` exclusive).
 */
export function costQuery({from, to}: DateRange): StudioCostQuery {
  const start = startOfDay(from)!;
  const end = startOfDay(to)!;
  end.setDate(end.getDate() + 1);
  return {from: start.toISOString(), to: end.toISOString(), take: JOBS_TAKE};
}

/** Whole dollars to the cent; amounts under a dollar to a hundredth of a cent. */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const digits = value !== 0 && Math.abs(value) < 1 ? 4 : 2;
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

// What each stage is part of, in the words of Studio's cost plan.
const STAGE_GROUPS: Record<string, string> = {
  vision: 'Document reading',
  transcription: 'Document reading',
  'document-indexing': 'Document reading',
  'document-ingest': 'Document reading',
  topics: 'Storyboard',
  storyboard: 'Storyboard',
  'source-check': 'Source check',
  rewrite: 'Text rewriting',
  quiz: 'Knowledge check',
  image: 'Image generation',
  stock: 'Image generation',
  'stock-pick': 'Image generation',
  voiceover: 'Voiceover',
  subtitles: 'Subtitles',
  'motion-capture': 'Motion graphics',
  'audio-mix': 'Rendering',
  'reel-export': 'Rendering',
  loudness: 'Rendering',
  finalize: 'Rendering',
  'clip-cut': 'Rendering',
  'stock-transcode': 'Rendering',
  publish: 'Publishing',
  checkpoint: 'Publishing',
};

const REASONS: Record<string, string> = {
  initial: 'Initial generation',
  replan: 'Storyboard planned again',
  'image-regenerated': 'Image regenerated',
  'prompt-edited': 'Image prompt edited',
  'narration-edited': 'Narration edited',
  'text-edited': 'On-screen text edited',
  'layout-changed': 'Layout changed',
  'scene-added': 'Scene added',
  'footage-regenerated': 'Footage regenerated',
  'footage-query-edited': 'Footage search edited',
  'style-changed': 'Brand style changed',
  'voice-changed': 'Voice changed',
  'config-changed': 'Settings changed',
  'runtime-changed': 'Renderer updated',
  'cache-miss': 'Cached file missing',
  'captions-changed': 'Captions changed',
  'check-after-edit': 'Check after an edit',
  rewrite: 'AI rewrite',
  quiz: 'Knowledge check',
};

const stageLabel = (stage: string) =>
  !stage
    ? text.unattributed
    : STAGE_GROUPS[stage]
    ? `${STAGE_GROUPS[stage]} (${stage})`
    : stage;
const reasonLabel = (reason: string) =>
  !reason ? text.unattributed : REASONS[reason] || reason;

/** A bucket's measured usage: tokens, characters, images and vCPU time it has. */
function usage(bucket: StudioCostBucket): string {
  return [
    bucket.tokens ? text.tokens(bucket.tokens) : '',
    bucket.characters ? text.characters(bucket.characters) : '',
    bucket.images ? text.images(bucket.images) : '',
    bucket.vcpuSeconds ? text.vcpuSeconds(bucket.vcpuSeconds) : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** A cost, and how many of its calls had an unknown cost. */
function Cost({
  usd,
  unknown,
}: {
  usd: number | null | undefined;
  unknown?: number;
}) {
  return (
    <>
      {formatUsd(usd)}
      {!!unknown && (
        <span className="StudioCostsMuted"> + {text.unknownCost(unknown)}</span>
      )}
    </>
  );
}

/**
 * The share of attributed known cost spent on the first generation, and on everything after,
 * with how many calls on each side had an unknown cost.
 */
function initialVsRegenerations(byReason: StudioCostBucket[]) {
  let initial = 0;
  let regenerations = 0;
  let initialUnknown = 0;
  let regenerationsUnknown = 0;
  for (const bucket of byReason) {
    if (!bucket.key) continue;
    const unknown = bucket.unknownCostReceipts ?? 0;
    if (bucket.key === 'initial') {
      initial += bucket.usd ?? 0;
      initialUnknown += unknown;
    } else {
      regenerations += bucket.usd ?? 0;
      regenerationsUnknown += unknown;
    }
  }
  const attributed = initial + regenerations;
  return {
    initial,
    regenerations,
    initialUnknown,
    regenerationsUnknown,
    percent:
      attributed > 0 ? Math.round((regenerations / attributed) * 100) : 0,
  };
}

function BreakdownTable({
  testId,
  title,
  column,
  rows,
  label,
  showUsage,
}: {
  testId: string;
  title: string;
  /** What the first column names: a stage, reason or model. */
  column: string;
  rows: StudioCostBucket[];
  label: (key: string) => string;
  showUsage?: boolean;
}) {
  return (
    <div className="StudioCostsBreakdown" data-testid={testId}>
      <h5>{title}</h5>
      {rows.length === 0 ? (
        <p className="StudioCostsMuted">{text.noRows}</p>
      ) : (
        <Table striped size="sm" responsive>
          <thead>
            <tr>
              <th>{column}</th>
              <th className="StudioCostsNumber">{text.cost}</th>
              <th className="StudioCostsNumber">{text.calls}</th>
              {showUsage && <th>{text.usage}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.key}-${index}`}>
                <td title={row.key || undefined}>{label(row.key)}</td>
                <td className="StudioCostsNumber">
                  <Cost usd={row.usd} unknown={row.unknownCostReceipts} />
                </td>
                <td className="StudioCostsNumber">
                  {row.calls?.toLocaleString('en-US') ?? '—'}
                </td>
                {showUsage && <td>{usage(row)}</td>}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

/** A job's totals by render, microreel, stage and reason, under its row. */
function JobDetails({job}: {job: StudioCostJob}) {
  const section = <T extends StudioCostBucket>(
    title: string,
    rows: T[],
    label: (row: T) => string,
  ) =>
    rows.length > 0 && (
      <div className="StudioCostsDetail">
        <h6>{title}</h6>
        <ul>
          {rows.map((row, index) => (
            <li key={`${row.key}-${index}`}>
              {label(row)}:{' '}
              <Cost usd={row.usd} unknown={row.unknownCostReceipts} />
            </li>
          ))}
        </ul>
      </div>
    );
  const credits = text.credits(job.chargedCredits, job.heldCredits);
  return (
    <div className="StudioCostsDetails" data-testid="studio-cost-job-details">
      {section(text.byRender, job.renders, row => text.render(row.number))}
      {section(
        text.byReel,
        job.reels,
        row => row.label || row.key || text.unattributed,
      )}
      {section(text.byStage, job.byStage, row => stageLabel(row.key))}
      {section(text.byReason, job.byReason, row => reasonLabel(row.key))}
      {credits && (
        <p className="StudioCostsMuted">
          {text.creditsLabel}
          {credits}
        </p>
      )}
    </div>
  );
}

function JobsTable({jobs}: {jobs: StudioCostJob[]}) {
  const [open, setOpen] = useState<string | null>(null);
  if (jobs.length === 0)
    return <p className="StudioCostsMuted">{text.noRows}</p>;
  return (
    <Table striped size="sm" responsive data-testid="studio-cost-jobs">
      <thead>
        <tr>
          <th>{text.job}</th>
          <th className="StudioCostsNumber">{text.renders}</th>
          <th className="StudioCostsNumber">{text.aiCostColumn}</th>
          <th className="StudioCostsNumber">{text.computeCostColumn}</th>
          <th className="StudioCostsNumber">{text.costPerMicroreel}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {jobs.map(job => {
          const expanded = open === job.jobId;
          return (
            <React.Fragment key={job.jobId}>
              <tr data-testid="studio-cost-job">
                <td>
                  <div title={job.jobId}>{job.title || text.untitled}</div>
                  {(job.organizationName || job.organizationId) && (
                    <div className="StudioCostsMuted">
                      {job.organizationName ||
                        text.workspaceId(job.organizationId!)}
                    </div>
                  )}
                </td>
                <td className="StudioCostsNumber">{job.renderCount}</td>
                <td className="StudioCostsNumber">
                  <Cost usd={job.aiUsd} unknown={job.unknownCostReceipts} />
                </td>
                <td className="StudioCostsNumber">
                  {formatUsd(job.computeUsd)}
                </td>
                <td className="StudioCostsNumber">
                  {job.reelCountReported ? (
                    <Cost
                      usd={costPerReel(job)}
                      unknown={job.unknownCostReceipts}
                    />
                  ) : (
                    // Without the job's microreel count, only the microreels that had a cost
                    // in the period are known, and the period's cost over them is no unit cost.
                    formatUsd(null)
                  )}
                  {job.reelCount > 0 && (
                    <div className="StudioCostsMuted">
                      {job.reelCountReported
                        ? text.microreels(job.reelCount)
                        : text.microreelsWithCosts(job.reelCount)}
                    </div>
                  )}
                </td>
                <td>
                  <Button
                    variant="link"
                    size="sm"
                    aria-expanded={expanded}
                    onClick={() => setOpen(expanded ? null : job.jobId)}>
                    {expanded ? text.hideDetails : text.details}
                  </Button>
                </td>
              </tr>
              {expanded && (
                <tr>
                  <td colSpan={6}>
                    <JobDetails job={job} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
}

const isEmpty = (report: StudioCostReport) =>
  report.jobs.length === 0 &&
  report.byStage.length === 0 &&
  !report.totals.aiUsd &&
  !report.totals.computeUsd &&
  !report.totals.unknownCostReceipts;

function Report({report}: {report: StudioCostReport}) {
  if (isEmpty(report))
    return (
      <p className="StudioCostsMuted" data-testid="studio-costs-empty">
        {text.noCosts}
      </p>
    );
  const {totals} = report;
  const split = initialVsRegenerations(report.byReason);
  return (
    <>
      <ul data-testid="studio-cost-totals">
        <li>
          {text.aiCost}
          <Cost usd={totals.aiUsd} unknown={totals.unknownCostReceipts} />
        </li>
        <li>
          {text.computeCost}
          {formatUsd(totals.computeUsd)}
        </li>
        <li>
          {text.totalCost}
          <Cost
            usd={addUsd(totals.aiUsd, totals.computeUsd)}
            unknown={totals.unknownCostReceipts}
          />
        </li>
        {totals.fleetUsd !== null && (
          <li>
            {text.fleetCost}
            {formatUsd(totals.fleetUsd)}
          </li>
        )}
        {!!totals.unattributedUsd && (
          <li>
            {text.unattributedCost}
            {formatUsd(totals.unattributedUsd)}
          </li>
        )}
        <li>
          {text.unknownCostReceipts}
          {totals.unknownCostReceipts.toLocaleString('en-US')}
        </li>
        {totals.claimPricedReceipts > 0 && (
          <li>
            {text.claimPricedReceipts}
            {totals.claimPricedReceipts.toLocaleString('en-US')}
          </li>
        )}
      </ul>
      {report.byReason.length > 0 && (
        <ul data-testid="studio-cost-initial-vs-regenerations">
          <li>
            {text.initialCost}
            <Cost usd={split.initial} unknown={split.initialUnknown} />
          </li>
          <li>
            {text.regenerationCost}
            <Cost
              usd={split.regenerations}
              unknown={split.regenerationsUnknown}
            />
            {split.initial + split.regenerations > 0 &&
              text.shareOfAttributed(split.percent)}
          </li>
        </ul>
      )}
      <div className="StudioCostsBreakdowns">
        <BreakdownTable
          testId="studio-cost-by-stage"
          title={text.byStage}
          column={text.stage}
          rows={report.byStage}
          label={stageLabel}
          showUsage
        />
        <BreakdownTable
          testId="studio-cost-by-reason"
          title={text.byReason}
          column={text.reason}
          rows={report.byReason}
          label={reasonLabel}
        />
        <BreakdownTable
          testId="studio-cost-by-model"
          title={text.byModel}
          column={text.model}
          rows={report.byModel}
          label={model => model || text.unattributed}
        />
      </div>
      <h5>{text.jobs}</h5>
      <JobsTable jobs={report.jobs} />
    </>
  );
}

function failureText(result: Extract<StudioCostsResult, {report: null}>) {
  switch (result.error) {
    case 'forbidden':
      return text.costsForbidden;
    case 'unavailable':
      return text.costsUnavailable;
    case 'invalid':
      return result.message || text.costsInvalid;
    default:
      return text.costsFailed;
  }
}

export interface StudioCostsProps {
  /** Reads the report; resolves to a failure rather than throwing. */
  load: (query: StudioCostQuery) => Promise<StudioCostsResult>;
  /** Today, for the default range; now when left out. */
  today?: Date;
}

/**
 * The SuperAdmin view of Studio's costs for a date range: totals, cost by stage, reason and
 * model, and each job's renders and costs.
 */
export default function StudioCosts({load, today}: StudioCostsProps) {
  const [range, setRange] = useState<DateRange>(() =>
    defaultRange(today ?? new Date()),
  );
  const [reloads, setReloads] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<StudioCostsResult | null>(null);
  const {from, to} = range;
  const problem = rangeProblem(range);

  useEffect(() => {
    if (problem) return;
    // Only the answer for the range shown counts.
    let current = true;
    setLoading(true);
    load(costQuery({from, to})).then(answer => {
      if (!current) return;
      setResult(answer);
      setLoading(false);
    });
    return () => {
      current = false;
    };
  }, [from, to, reloads, load, problem]);

  return (
    <div className="StudioCosts" data-testid="studio-costs">
      <p className="StudioCostsMuted">{text.studioCostsNote}</p>
      <Form
        className="StudioCostsRange"
        onSubmit={event => {
          event.preventDefault();
          setReloads(n => n + 1);
        }}>
        <Form.Group controlId="studio-costs-from">
          <Form.Label>{text.from}</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={range.from}
            max={range.to}
            onChange={event => setRange({...range, from: event.target.value})}
          />
        </Form.Group>
        <Form.Group controlId="studio-costs-to">
          <Form.Label>{text.to}</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={range.to}
            min={range.from}
            onChange={event => setRange({...range, to: event.target.value})}
          />
        </Form.Group>
        <Button
          type="submit"
          variant="outline-secondary"
          size="sm"
          disabled={!!problem || loading}>
          {text.refresh}
        </Button>
      </Form>
      {problem ? (
        <p role="alert" className="StudioCostsProblem">
          {problem}
        </p>
      ) : loading ? (
        <p className="StudioCostsMuted" role="status">
          {text.loadingCosts}
        </p>
      ) : result?.report ? (
        <Report report={result.report} />
      ) : (
        result && (
          <p role="alert" className="StudioCostsProblem">
            {failureText(result)}
          </p>
        )
      )}
    </div>
  );
}
